import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { get, post } from "../lib/api";
import { useLoad, useQueryState } from "../lib/hooks";
import { COMPANY_STATUSES, LINKEDIN_STATUSES, RELATIONSHIP_TYPES, humanize, optionLabel } from "../lib/format";
import { formatDate } from "../../helpers/datetime";
import { Badge, Button, Card, Checkbox, Empty, ErrorBox, Loading, Modal, Pager, Segment, Spinner, TierBadge } from "../ui";
import { useShell } from "../shell/context";

// Fields the merge can take from either side (docs/crm/api.md, E7 merge).
const CONTACT_FIELDS = [
    ["name", "Name"],
    ["email", "Email"],
    ["linkedin_url", "LinkedIn URL"],
    ["phone", "Phone"],
    ["tier", "Tier"],
    ["relationship_type", "Relationship", (v) => optionLabel(RELATIONSHIP_TYPES, v)],
    ["how_we_met", "How we met"],
    ["location", "Location"],
    ["cadence_days_override", "Custom cadence"],
    ["connected_on", "Connected on"],
    ["linkedin_status", "LinkedIn status", (v) => optionLabel(LINKEDIN_STATUSES, v)],
    ["notes", "Notes"],
];
const COMPANY_FIELDS = [
    ["name", "Name"],
    ["domain", "Domain"],
    ["website", "Website"],
    ["country", "Country"],
    ["industry", "Industry"],
    ["size_band", "Size"],
    ["status", "Status", (v) => optionLabel(COMPANY_STATUSES, v)],
    ["score_financial", "Financial capacity"],
    ["score_engagement", "Engagement readiness"],
    ["score_fit", "Strategic fit"],
    ["score_access", "Accessibility"],
    ["keep_in_touch", "Keep in touch", (v) => (v ? "yes" : "no")],
    ["cadence_days", "Cadence days"],
    ["linked_crawler_company_id", "Crawler company"],
    ["notes", "Notes"],
];

const isEmpty = (v) => v === null || v === undefined || v === "" || v === 0 || v === false;
const show = (v, fmt) => (v === null || v === undefined || v === "" ? <em className="hz-muted">empty</em> : fmt ? fmt(v) : String(v));

// Keep `keep`, drop the other. For each field the default winner is the kept
// value, or the dropped one when the kept field is empty (the API's rule).
function MergeModal({ entity, pair, onClose, onMerged }) {
    const { toast, bump } = useShell();
    const base = entity === "company" ? "companies" : "contacts";
    const fields = entity === "company" ? COMPANY_FIELDS : CONTACT_FIELDS;
    const [keep, setKeep] = useState("a");
    const [pick, setPick] = useState({});
    const [confirm, setConfirm] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const a = useLoad(() => get(`v1/crm/${base}/${pair.a.id}`), [pair.a.id]);
    const b = useLoad(() => get(`v1/crm/${base}/${pair.b.id}`), [pair.b.id]);
    const recs = { a: a.data, b: b.data };
    const drop = keep === "a" ? "b" : "a";

    useEffect(() => {
        if (!a.data || !b.data) return;
        const next = {};
        fields.forEach(([k]) => {
            next[k] = isEmpty(recs[keep][k]) && !isEmpty(recs[drop][k]) ? drop : keep;
        });
        setPick(next);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [a.data, b.data, keep]);

    const differing = useMemo(
        () => (a.data && b.data ? fields.filter(([k]) => JSON.stringify(a.data[k] ?? null) !== JSON.stringify(b.data[k] ?? null)) : []),
        [a.data, b.data, fields]
    );

    const merge = async () => {
        setBusy(true);
        setError(null);
        const body = { keep_id: recs[keep].id, drop_id: recs[drop].id, confirm: true, fields: {} };
        differing.forEach(([k]) => {
            if (pick[k] === drop) body.fields[k] = "drop";
        });
        try {
            await post(`v1/crm/merge/${base}`, body);
            toast(`Merged into ${recs[keep].name}.`, { icon: "ri-git-merge-line" });
            bump();
            onMerged(recs[keep].id);
        } catch (e) {
            setError(e.message);
            setBusy(false);
        }
    };

    const loaded = a.data && b.data;
    const col = (side) => {
        const r = recs[side];
        return (
            <label className="hz-merge-pick">
                <input type="radio" name="keep" checked={keep === side} onChange={() => setKeep(side)} />
                <span>
                    <span className="hz-strong">Keep {r.name}</span>
                    <span className="hz-small hz-muted" style={{ display: "block" }}>Created {formatDate(r.created_at)}</span>
                </span>
            </label>
        );
    };

    return (
        <Modal
            open
            title={`Merge ${entity === "company" ? "companies" : "people"}`}
            onClose={onClose}
            width={760}
            footer={
                loaded && (
                    <>
                        <span className="hz-spacer">
                            <Checkbox checked={confirm} onChange={setConfirm}>A merge cannot be undone. Merge {recs[drop].name} into {recs[keep].name}.</Checkbox>
                        </span>
                        <Button variant="light" onClick={onClose}>Cancel</Button>
                        <Button variant="danger" icon="ri-git-merge-line" disabled={!confirm || busy} onClick={merge}>
                            {busy && <Spinner />}Merge
                        </Button>
                    </>
                )
            }
        >
            {(a.error || b.error) && <ErrorBox error={a.error || b.error} />}
            {!loaded && !a.error && !b.error && <Loading />}
            {loaded && (
                <>
                    {error && <div className="hz-alert hz-alert-danger" style={{ marginBottom: 12 }}>{error}</div>}
                    <p className="hz-muted" style={{ marginTop: 0 }}>
                        Roles, touches, leads, tags{entity === "contact" ? " and tier history" : " and signals"} of both move to the kept record. The other one goes to the trash and cannot be restored. Pick which value wins where they differ.
                    </p>
                    <div className="hz-table-wrap">
                        <table className="hz-table hz-merge-table">
                            <thead>
                                <tr>
                                    <th style={{ width: 160 }}>Field</th>
                                    <th>{col("a")}</th>
                                    <th>{col("b")}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {fields.map(([k, label, fmt]) => {
                                    const differs = differing.some(([d]) => d === k);
                                    return (
                                        <tr key={k}>
                                            <td className="hz-strong">{label}</td>
                                            {["a", "b"].map((side) => (
                                                <td key={side}>
                                                    {differs ? (
                                                        <label className="hz-merge-pick">
                                                            <input type="radio" name={`f-${k}`} checked={pick[k] === side} onChange={() => setPick((p) => ({ ...p, [k]: side }))} />
                                                            <span style={{ wordBreak: "break-word" }}>{show(recs[side][k], fmt)}</span>
                                                        </label>
                                                    ) : (
                                                        <span className="hz-muted" style={{ wordBreak: "break-word" }}>{show(recs[side][k], fmt)}</span>
                                                    )}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                                <tr>
                                    <td className="hz-strong">Tags</td>
                                    <td>{(recs.a.tags || []).join(", ") || <em className="hz-muted">none</em>}</td>
                                    <td>{(recs.b.tags || []).join(", ") || <em className="hz-muted">none</em>}</td>
                                </tr>
                                {entity === "contact" && (
                                    <tr>
                                        <td className="hz-strong">Roles</td>
                                        {["a", "b"].map((side) => (
                                            <td key={side} className="hz-small">
                                                {(recs[side].roles || []).map((r) => (
                                                    <div key={r.id}>{r.title || "No title"} at {r.company_name}{r.is_current ? "" : " (past)"}</div>
                                                ))}
                                                {(recs[side].roles || []).length === 0 && <em className="hz-muted">none</em>}
                                            </td>
                                        ))}
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </Modal>
    );
}

const sideLine = (entity, s) => {
    if (entity === "company") return [s.domain, s.country].filter(Boolean).join(" · ");
    const role = s.current_role ? [s.current_role.title, s.current_role.company_name].filter(Boolean).join(" at ") : "";
    return [role, s.email, s.linkedin_url ? "LinkedIn" : ""].filter(Boolean).join(" · ");
};

export default function Duplicates() {
    const [entity, setEntity] = useQueryState("entity", "contact");
    const [page, setPage] = useQueryState("page", 1);
    const [open, setOpen] = useState(null);
    const { version } = useShell();
    const { data, error, loading, reload } = useLoad(() => get("v1/crm/duplicates", { entity, page, page_size: 25 }), [entity, page, version]);
    const rows = (data && data.data) || [];
    const base = entity === "company" ? "/crm/companies/" : "/crm/contacts/";

    return (
        <Card
            noBody
            title="Possible duplicates"
            actions={<Segment options={[{ value: "contact", label: "People" }, { value: "company", label: "Companies" }]} value={entity} onChange={(v) => { setEntity(v); setPage(1); }} />}
        >
            <ErrorBox error={error} onRetry={reload} />
            {loading && !data && <Loading />}
            {data && rows.length === 0 && !error && <Empty>No duplicates found.</Empty>}
            {rows.map((p, i) => (
                <div key={p.a.id + p.b.id} className="hz-list-item">
                    <Badge color="warning" title="Why they look alike">{humanize(p.match)}</Badge>
                    {[p.a, p.b].map((s, j) => (
                        <div key={s.id} className="hz-spacer" style={{ minWidth: 0 }}>
                            <div className="hz-row" style={{ gap: 6 }}>
                                <Link to={base + s.id} className="hz-strong">{s.name}</Link>
                                {entity === "contact" && <TierBadge tier={s.tier} />}
                            </div>
                            <div className="hz-small hz-muted hz-ellipsis">{sideLine(entity, s) || "No details"}</div>
                        </div>
                    ))}
                    <Button size="sm" variant="soft" icon="ri-git-merge-line" onClick={() => setOpen(p)}>Review</Button>
                </div>
            ))}
            {data && <Pager page={page} maxPage={data.max_page} count={data.count} onPage={setPage} label="pairs" />}
            {open && (
                <MergeModal
                    entity={entity}
                    pair={open}
                    onClose={() => setOpen(null)}
                    onMerged={() => {
                        setOpen(null);
                        reload();
                    }}
                />
            )}
        </Card>
    );
}
